<template>
  <b-overlay :show="processing" rounded="sm" class="overlay-display">
    <template #overlay>
      <div class="text-center">
        <b-spinner type="grow" variant="info" label="Loading..."></b-spinner>
        <p id="cancel-label">{{ processingMessage }}</p>
      </div>
    </template>
    <ErrorAlert
      :show="alert.error"
      :message="alert.message"
      @hide-alert="hideError"
    />
    <SuccessAlert
      :show="alert.success"
      :message="alert.message"
      @hide-alert="hideSuccess"
    />

    <div class="container-fluid p-0">
      <div class="row">
        <div class="col-auto">
          <h3><strong>Query Delivery Window</strong></h3>
        </div>
      </div>

      <div class="row mt-3 mb-0 form">
        <div class="col-md-12 col-xl-12 col-12">
          <div class="form-card">
            <b-form id="form">
              <div class="row mt-3">
                <div class="col-sm">
                  <b-form-group>
                    <label class="mt-1 report-label">Search Address:</label>
                    <b-form-input
                      id="address-autocomplete"
                      ref="addressInput"
                      v-model="form.addressSearch"
                      type="text"
                      maxlength="200"
                      placeholder="e.g. 50 Willow Lane, Winnipeg"
                      class="input-height mt-1"
                    ></b-form-input>
                    <small class="text-muted">
                      Start typing a Canadian address to search
                    </small>
                  </b-form-group>
                </div>
              </div>
              <div class="row mt-3">
                <div class="col-sm">
                  <b-form-group>
                    <label class="mt-1 report-label"
                      >Province:</label
                    >
                    <b-form-select
                      v-model="form.province"
                      v-bind:disabled="isReadOnly"
                      :options="provincesOpt"
                      @change="selectProvince"
                      class="input-height mt-1"
                    ></b-form-select>
                  </b-form-group>
                </div>
                <div class="col-sm">
                  <b-form-group>
                    <label class="mt-1 report-label"
                      >City:</label
                    >
                    <v-select
                      :options="citiesOpt"
                      v-model="form.city"
                      v-bind:disabled="isReadOnly"
                      @input="selectCity"
                      :class="[
                        'input-height',
                        'mt-1'
                      ]"
                    ></v-select>
                  </b-form-group>
                </div>
                <div class="col-sm">
                  <b-form-group>
                    <label class="mt-1 report-label"
                      >Region:</label
                    >
                    <b-form-input
                      v-model="form.region"
                      type="text"
                      readonly
                      placeholder="Region"
                      class="input-height mt-1"
                    ></b-form-input>
                  </b-form-group>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-sm">
                  <b-form-group>
                    <label class="mt-0 report-label">Postal Code:</label>
                    <b-form-input
                      v-model="form.postalCode"
                      v-bind:readonly="isReadOnly"
                      :state="postalCodeState"
                      maxlength="7"
                      type="text"
                      placeholder="Please input Postal Code"
                      class="input-height mt-1"
                    ></b-form-input>
                  </b-form-group>
                </div>
                <div class="col-sm">
                  <b-form-group>
                    <label class="mt-1 report-label">Delivery date:</label>
                    <b-form-datepicker
                      :date-format-options="{
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                      }"
                      v-model="form.deliveryDateNew"
                      v-bind:disabled="isReadOnly"
                      :date-disabled-fn="dateDisabled"
                      class="mb-2"
                      required
                    ></b-form-datepicker>
                  </b-form-group>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-sm">
                  <b-button
                    type="button"
                    variant="primary"
                    class="submit-button"
                    @click="queryDeliveryWindow"
                  >
                    Query
                  </b-button>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-sm">
                  <label class="mt-1 report-label">Delivery Window:</label>
                  <b-table
                    v-if="deliveryRates.length > 0"
                    :items="deliveryRates"
                    :fields="deliveryRateFields"
                    bordered
                    small
                    responsive
                    class="mt-2"
                  >
                    <template #cell(status)="{ item }">
                      <span :class="item.pastCutoff ? 'text-danger' : 'text-success'">
                        {{ item.pastCutoff ? '⚠️ Past cutoff' : '✅ Available' }}
                      </span>
                    </template>
                  </b-table>
                  <p v-else-if="hasQueried" class="text-muted mt-2">
                    No delivery windows available for this date.
                  </p>
                </div>
              </div>
            </b-form>
          </div>
        </div>
      </div>
    </div>
  </b-overlay>
</template>

<script>
import dayjs from "dayjs";
import ToastMixin from "@/mixin/toast-mixin";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import weekOfYear from "dayjs/plugin/weekOfYear";
import isoWeek from "dayjs/plugin/isoWeek";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

import ErrorAlert from "@/components/alerts/error.vue";
import SuccessAlert from "@/components/alerts/success.vue";
import {
  getAllCities,
  getCutoffs,
  getCutoffsByRegionId
} from "@/utils/queryUtil";

import FormatMixin from "@/mixin/format-mixin";

export default {
  mixins: [FormatMixin, ToastMixin],
  components: {
    ErrorAlert,
    SuccessAlert
  },
  computed: {
    postalCodeState() {
      return true;
      // return this.form.postalCode && this.form.postalCode.length >= 6
      //   ? true
      //   : false;
    }
  },
  data() {
    return {
      processing: false,
      processingMessage: "Please wait...",
      result: "",
      isReadOnly: false,
      form: {
        postalCode: "",
        deliveryDate: "",
        deliveryDateNew: "",
        city: "",
        province: "",
        region: "",
        addressSearch: "",
        lang: "en-ca"
      },

      alert: {
        error: false,
        message: ""
      },
      deliveryRates: [],
      hasQueried: false,
      deliveryRateFields: [
        { key: "name", label: "Delivery Window" },
        { key: "applicableDate", label: "Order By" },
        { key: "cutoffTime", label: "Cutoff Time(CST)" },
        { key: "status", label: "Status" }
      ],
      provincesOpt: [],
      citiesOpt: [],
      provinceCityObj: {},
      cityRegionObj: {},
      allCitiesObj: {},
      citiesObj: {},

      regionId: "",

      autocomplete: null,
      googleMapsLoaded: false,
      shippingMethodsOpt: [],
      closedDays: [],
      unavailableDates: [],
      availableDates: [],
      cutoffTime: []
    };
  },
  async mounted() {
    this.queryCities();
    await this.loadGoogleMapsAPI();
  },
  beforeDestroy() {
    if (this.autocomplete) {
      window.google?.maps?.event?.clearInstanceListeners(this.autocomplete);
    }
  },
  methods: {
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    async queryCities() {
      // this.displayMessage("Success", "Getting cities...");
      const response = await getAllCities();
      if (response) {
        this.allCitiesObj = response;
        this.citiesObj = {};
        for (const key of Object.keys(this.allCitiesObj)) {
          const provinceName = key;
          const cities = [];
          const province = { text: provinceName, value: provinceName };
          for (const val of this.allCitiesObj[key]) {
            for (const city of val.cities) {
              cities.push(`${city.name}(${val.shortCode})`);
              this.cityRegionObj[`${city.name}(${val.shortCode})`] =
                val.regionTag;

              const cityObj = {
                text: `${city.name}`,
                region: val.regionTag,
                regionId: val.regionId,
                cityId: city.id
              };
              // this.citiesObj[city.name] = cityObj;
              this.citiesObj[`${city.name}(${val.shortCode})`] = cityObj;
            }
          }
          this.provincesOpt.push(province);
          this.provinceCityObj[provinceName] = cities.sort((a, b) =>
            a > b ? 1 : -1
          );
        }
        this.provincesOpt = this.provincesOpt.sort((a, b) =>
          a.value > b.value ? 1 : -1
        );
        // this.displayMessage("Success", "Query Cities: Done...");
        return true;
      } else {
        this.displayMessage("warning", `Query Cities Exception`);
      }
    },

    selectProvince() {
      this.$set(this.form, "city", "");
      this.$set(this.form, "region", "");
      console.log("selectProvince:", this.form.province);
      this.citiesOpt = this.provinceCityObj[this.form.province];
    },
    async selectCity() {
      console.log("selectCity:", this.form.city);
      this.form.region = "";
      if (this.form.city) {
        const cityObj = this.citiesObj[this.form.city];
        if (cityObj) {
          this.regionId = cityObj.regionId;
          await this.queryCutoffs();
        }

        const region = this.cityRegionObj[this.form.city];
        if (region) {
          this.$set(this.form, "region", region);
          console.log("region:", this.form.region);
        } else {
          this.displayMessage(
            "warning",
            `Region does not exist for city ${this.form.city} `
          );
          this.form.city = "";
        }
      } else {
        this.displayMessage("warning", `City is empty`);
      }
    },

    clearForm() {
      this.form = {
        postalCode: "",
        deliveryDate: "",
        deliveryDateNew: "",
        regionId: ""
      };
    },

    async queryCutoffs() {
      this.displayMessage("Success", "Getting cutoffs...");
      // const region = this.form.region;
      const regionId = this.regionId;
      if (!regionId) {
        return;
      }
      const cutoffs = await getCutoffsByRegionId(regionId);
      if (cutoffs) {
        if (cutoffs.closedDates) {
          this.closedDays = cutoffs.closedDates.map(closeD => parseInt(closeD));
        }
        this.unavailableDates = cutoffs.un_available;
        this.availableDates = cutoffs.available;
        this.cutoffTime = cutoffs.cutoff_time;
        // this.displayMessage("Success", "Cutoffs done...");
        return;
      } else {
        this.displayMessage("warning", `Query Cutoffs Exception`);
      }
    },

    async queryDeliveryWindow() {
      this.deliveryRates = [];
      this.hasQueried = false;
      if ((!this.form.postalCode || this.form.postalCode.length < 6) && !this.form.city) {
        this.displayMessage("warning", `Please select City or input postal code`);
        return false;
      }
      if (!this.form.deliveryDateNew) {
        this.displayMessage("warning", `Please select delivery date`);
        return;
      }

      // this.displayMessage("Success", "Getting Delivery window...");
      this.processing = true;
      this.displayMessage("Success", "Getting data...");
      if (this.form.postalCode) {
        this.form.postalCode = this.form.postalCode.toUpperCase();
        await this.queryDeliveryRates(
          this.form.postalCode,
          this.form.deliveryDateNew,
          this.form.lang
        );
      } else {
        const cityObj = this.citiesObj[this.form.city];
        const townId = cityObj.cityId;
        console.log("townId:", townId);
        await this.queryDeliveryRatesByTown(
          townId,
          this.form.deliveryDateNew,
          this.form.lang
        );
      }
      this.hasQueried = true;
      this.processing = false;
    },
    async queryDeliveryRates(postal, deliveryDate, lang) {
      const postalNew = postal.replace(/\s+/g, "");
      const rateUrl = `https://shop.callia.com/rates/delivery/postalcode/${postalNew}/date/${deliveryDate}?lang=${lang}`;
      const rateList = await this.axios
        .get(rateUrl)
        .then(res => {
          console.log("queryDeliveryRates response:", res.data);
          return JSON.parse(JSON.parse(res.data.body)).sort((a, b) => {
            return (
              Number(a.zone_rate.replace("$", "")) -
              Number(b.zone_rate.replace("$", ""))
            );
          });
        })
        .catch(err => {
          console.log("error:", err);
          this.displayMessage(
            "warning",
            `Query DeliveryRates Exception: ${err}`
          );
        });
      this.handleRates(rateList);
    },
    async queryDeliveryRatesByTown(townId, deliveryDate, lang) {
      const rateUrl = `https://shop.callia.com/rates/delivery/town/${townId}/date/${deliveryDate}?lang=${lang}`;
      const rateList = await this.axios
        .get(rateUrl)
        .then(res => {
          console.log("queryDeliveryRatesByTown response:", res.data);
          return JSON.parse(JSON.parse(res.data.body)).sort((a, b) => {
            return (
              Number(a.zone_rate.replace("$", "")) -
              Number(b.zone_rate.replace("$", ""))
            );
          });
        })
        .catch(err => {
          console.log("error:", err);
          this.displayMessage(
            "warning",
            `Query DeliveryRates Exception: ${err}`
          );
        });
      this.handleRates(rateList);
    },
    handleRates(rateList) {
      console.log("rateList:", rateList);
      this.deliveryRates = [];
      if (rateList) {
        for (const rate of rateList) {
          if (rate.status === true) {
            let applicableDate;
            if (rate.applicable_date == 0) {
              applicableDate = "Same day";
            } else if (rate.applicable_date == 1) {
              applicableDate = "Day before";
            } else {
              applicableDate = `${rate.applicable_date} day before`;
            }

            const currentTimeStamp = dayjs().tz("America/Winnipeg");
            const deliveryDay = dayjs.tz(this.form.deliveryDateNew, "America/Winnipeg");
            const cutoffDay = deliveryDay.subtract(parseInt(rate.applicable_date), "d");

            let cutoffTime;
            let pastCutoffTime = false;
            if (rate.applicable_end_time) {
              const [cutoffHour, cutoffMin] = rate.applicable_end_time.split(/:|\./);
              const cutoffDeadline = cutoffDay
                .startOf("d")
                .add(parseInt(cutoffHour), "h")
                .add(parseInt(cutoffMin), "m");
              pastCutoffTime = currentTimeStamp.isAfter(cutoffDeadline);
              cutoffTime = rate.applicable_end_time;
            } else {
              cutoffTime = "No cutoff";
              pastCutoffTime = currentTimeStamp.isAfter(cutoffDay.endOf("day"));
            }

            this.deliveryRates.push({
              name: rate.name,
              applicableDate: `${applicableDate}(${cutoffDay.format("YYYY-MM-DD")})`,
              cutoffTime,
              pastCutoff: pastCutoffTime
            });
          }
        }
      }
    },

    dateDisabled(ymd, date) {
      const weekday = date.getDay();
      const dateSelected = dayjs.tz(ymd, "America/Winnipeg");
      const currentTimeStamp = dayjs().tz("America/Winnipeg");
      const today = currentTimeStamp.startOf("day");
      const formatedDate = dateSelected.format("YYYY-MM-DD");
      if (dateSelected.isBefore(today)) {
        return true;
      }
      if (this.availableDates && this.availableDates.length > 0) {
        for (const availableDates of this.availableDates) {
          if (availableDates.date.includes(formatedDate)) {
            const cutOffTimeStamp = dateSelected
              .subtract(parseInt(availableDates.cutoff_effect), "d")
              .startOf("d")
              .add(availableDates.cutoff_time.split(/:|\./)[0], "h")
              .add(availableDates.cutoff_time.split(/:|\./)[1], "m");

            if (cutOffTimeStamp.isAfter(currentTimeStamp)) {
              return false;
            }
          }
        }
      }
      if (this.unavailableDates.includes(formatedDate)) {
        return true;
      }
      if (this.closedDays.includes(weekday)) {
        return true;
      }

      const cutoff = this.cutoffTime.find(df => +df.day === dateSelected.day());
      //Calculate the cutoff time (d - cutoff_effect)
      if (cutoff) {
        const cutOffTimeStamp = dateSelected
          .subtract(parseInt(cutoff.cutoff_effect), "d")
          .startOf("d")
          .add(cutoff.cutoff_time.split(/:|\./)[0], "h")
          .add(cutoff.cutoff_time.split(/:|\./)[1], "m");

        return cutOffTimeStamp.isBefore(currentTimeStamp);
      } else {
        // console.log("no cutoff", dateSelected.day());
        return true;
      }
    },

    hideError() {
      this.alert.error = false;
    },
    hideSuccess() {
      this.alert.success = false;
    },
    checkItems() {
      if (!this.form.deliveryDateNew) {
        this.displayMessage("warning", `Delivery Date can't be empty`);
        return false;
      }
      if (!this.form.postalCode) {
        this.displayMessage("warning", `Postal Code can't be empty`);
        return false;
      }
      return true;
    },

    loadGoogleMapsAPI() {
      return new Promise(resolve => {
        // Already fully loaded
        if (window.google && window.google.maps && window.google.maps.places) {
          this.googleMapsLoaded = true;
          this.$nextTick(() => this.initAutocomplete());
          resolve();
          return;
        }

        // Another component already injected the script tag — reuse it
        const existing = document.querySelector(
          'script[src*="maps.googleapis.com/maps/api/js"]'
        );
        if (existing) {
          const onReady = () => {
            this.googleMapsLoaded = true;
            this.$nextTick(() => this.initAutocomplete());
            resolve();
          };
          // Script finished loading before we attached our listener
          if (window.google && window.google.maps && window.google.maps.places) {
            onReady();
          } else {
            existing.addEventListener("load", onReady);
          }
          return;
        }

        const apiKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;
        if (!apiKey) {
          console.warn(
            "Google Maps API key not found in environment variables. Address search will not be available."
          );
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=Function.prototype`;
        script.async = true;
        script.defer = true;

        script.onload = () => {
          this.googleMapsLoaded = true;
          this.$nextTick(() => this.initAutocomplete());
          resolve();
        };

        script.onerror = () => {
          console.error(
            "Failed to load Google Maps API. Please check your API key."
          );
          resolve();
        };

        document.head.appendChild(script);
      });
    },

    initAutocomplete() {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        return;
      }

      const input = this.$refs.addressInput?.$el;
      if (!input) {
        return;
      }

      this.autocomplete = new window.google.maps.places.Autocomplete(input, {
        componentRestrictions: { country: "ca" },
        fields: ["address_components", "formatted_address"],
        types: ["address"]
      });

      this.autocomplete.addListener("place_changed", () => {
        this.handlePlaceSelect();
      });
    },

    async queryPostalCode() {
      if (!this.form.postalCode || this.form.postalCode.length < 6) {
        return false;
      }
      this.processing = true;
      this.displayMessage("Success", "Getting data...");

      try {
        const postalNew = this.form.postalCode.replace(/\s+/g, "");
        const queryUrl = `${process.env.VUE_APP_API_ADMIN_API_ENDPOINT}/places/postalcodes/query/${postalNew}`;
        const responseList = await this.axios
          .get(queryUrl)
          .then(res => {
            console.log("queryPostalCode response:", res.data);
            return res.data.result;
          })
          .catch(err => {
            console.log("error:", err);
            this.displayMessage("warning", `queryPostalCode Exception: ${err}`);
          });
        if (!responseList || responseList.length === 0) {
          this.form.information = "We Don't deliver there";
          // this.clearForm();
          this.processing = false;
          return;
        }
        this.form.region = responseList[0].region_name;
        this.regionId = responseList[0].region_id;
        const province = responseList[0].province_name;

        const provinceObj = this.provincesOpt.find(
          p =>
          p.value === province ||
          p.text === province
        );
        if (provinceObj) {
          this.form.province = provinceObj.value;
          // Populate citiesOpt without clearing deliveryDateNew (we do it manually)
          this.citiesOpt = this.provinceCityObj[provinceObj.value] || [];

          const city = responseList[0].town_name;
          // ── City ───────────────────────────────────────────────────────────────
          if (city && this.citiesOpt.length) {
            const normalizedCity = city.toLowerCase();

            // Exact match on the name portion before "(shortCode)", then partial
            const cityOption =
              this.citiesOpt.find(
                c => c.split("(")[0].trim().toLowerCase() === normalizedCity
              ) ||
              this.citiesOpt.find(c => {
                const name = c.split("(")[0].trim().toLowerCase();
                return (
                  name.includes(normalizedCity) || normalizedCity.includes(name)
                );
              });

            if (cityOption) {
              console.log("cityOption:", cityOption);
              this.form.city = cityOption;
              await this.selectCity();
              // Resolve region from city
              const region = this.cityRegionObj[cityOption];
              if (region) {
                this.$set(this.form, "region", region);
              }
            } else {
              this.displayMessage(
                "warning",
                `City "${city}" not found in system. We might not delivery there`
              );
            }
          }
        }

        // this.selectCity();

        // this.queryCutoffs();
        // console.log("queryPostalCode responseList:", responseList);
      } catch (e) {
        console.error("query error:", e);
      }

      this.processing = false;
    },

    async handlePlaceSelect() {
      const place = this.autocomplete.getPlace();

      if (!place || !place.address_components) {
        return;
      }

      let city = "";
      let province = "";
      let provinceLong = "";
      let postalCode = "";
      let country = "";

      for (const component of place.address_components) {
        const types = component.types;
        if (types.includes("locality")) {
          city = component.long_name
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");
        }
        if (types.includes("administrative_area_level_1")) {
          province = component.short_name
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");
          provinceLong = component.long_name
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");
        }
        if (types.includes("postal_code")) {
          postalCode = component.long_name;
        }
        if (types.includes("country")) {
          country = component.short_name;
        }
      }

      if (country !== "CA") {
        this.displayMessage("warning", "Please select a Canadian address");
        return;
      }

      // ── Postal code ──────────────────────────────────────────────────────────
      if (postalCode) {
        const clean = postalCode.replace(/\s+/g, "").toUpperCase();
        this.form.postalCode =
          clean.length === 6
            ? `${clean.slice(0, 3)} ${clean.slice(3)}`
            : postalCode.toUpperCase();
      } else {
        this.displayMessage(
          "warning",
          "No postal code found for this address. Please enter it manually."
        );
      }

      // ── Province ─────────────────────────────────────────────────────────────
      const provinceObj = this.provincesOpt.find(
        p =>
          p.value === province ||
          p.value === provinceLong ||
          p.text === province ||
          p.text === provinceLong
      );

      if (provinceObj) {
        this.form.province = provinceObj.value;
        // Populate citiesOpt without clearing deliveryDateNew (we do it manually)
        this.citiesOpt = this.provinceCityObj[provinceObj.value] || [];

        // ── City ───────────────────────────────────────────────────────────────
        if (city && this.citiesOpt.length) {
          const normalizedCity = city.toLowerCase();

          // Exact match on the name portion before "(shortCode)", then partial
          const cityOption =
            this.citiesOpt.find(
              c => c.split("(")[0].trim().toLowerCase() === normalizedCity
            ) ||
            this.citiesOpt.find(c => {
              const name = c.split("(")[0].trim().toLowerCase();
              return (
                name.includes(normalizedCity) || normalizedCity.includes(name)
              );
            });

          if (cityOption) {
            console.log("cityOption:", cityOption);
            this.form.city = cityOption;
            await this.selectCity();
            // Resolve region from city
            const region = this.cityRegionObj[cityOption];
            if (region) {
              this.$set(this.form, "region", region);
            }
          } else {
            this.displayMessage(
              "warning",
              `City "${city}" not found in system. We might not delivery there.`
            );
          }
        }
      } else {
        this.displayMessage(
          "warning",
          `Province "${provinceLong || province}" not found — please select manually.`
        );
      }

      // ── Auto-query if date already selected ──────────────────────────────────
      if (this.form.deliveryDateNew && this.form.postalCode?.length >= 6) {
        await this.queryDeliveryWindow();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";
@import "@/assets/scss/form.scss";
</style>

<style>
/* Ensure the Google Maps autocomplete dropdown appears above Bootstrap modals */
.pac-container {
  z-index: 10000 !important;
}
</style>
